import { memo } from 'react';

import { Progress } from '@/components/ui/progress';
import i18n from '@/core/i18n';
import { cn } from '@/lib/utils';

import { getProgressColor } from '../../../helpers/progress-color.helper';

interface ApprovalProgressCellProps {
  /** Approved-for-invoice ÷ booked. The money columns use the same value. */
  percentage: number;
  /** Approved-for-invoice hours behind the ratio — rendered as the left mini label. */
  approvedForInvoiceHours: number;
  /** Booked hours behind the ratio — rendered as the right mini label (or "Not booked"). */
  bookedHours: number;
  /** Render the percentage label in a heavier weight (used for project rows). */
  emphasized?: boolean;
}

/**
 * Approved/booked ratio for aggregate (project/member) rows, styled like the
 * time-tracking day-view project header: mini hour labels above a colored
 * progress bar with the percentage on the right. Leaf time-entry rows show
 * approve/revert actions in this column instead.
 */
function ApprovalProgressCellComponent({ percentage }: ApprovalProgressCellProps) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1 py-0.5">
      <div className="flex items-center gap-2">
        <Progress
          value={Math.min(percentage, 100)}
          indicatorColor={getProgressColor(percentage)}
          className="h-2 min-w-0 flex-1"
        />
        <span
          className={cn('w-9 shrink-0 text-right text-xs tabular-nums font-medium text-foreground')}
        >
          {i18n.t('authenticated.timeTracking:project.progressPercentage', {
            percentage: percentage.toFixed(0),
          })}
        </span>
      </div>
    </div>
  );
}

/** Memoized so an unrelated row's selection toggle doesn't re-render every progress cell. */
export const ApprovalProgressCell = memo(ApprovalProgressCellComponent);
